/**
 * Reusable PDF components using @react-pdf/renderer
 * These components provide consistent styling across all PDF exports
 */

import React from 'react';
import { View, Text, Image } from '@react-pdf/renderer';
import { baseStyles, colors, getConnectionColor, getPriorityColor } from './styles';

// ============ CLASSIFICATION BANNERS ============

interface ClassificationBannerProps {
  classification: string;
  position: 'top' | 'bottom';
}

export const ClassificationBanner: React.FC<ClassificationBannerProps> = ({ 
  classification, 
  position 
}) => (
  <View style={position === 'top' ? baseStyles.classificationBanner : baseStyles.classificationBannerBottom}>
    <Text style={baseStyles.classificationText}>{classification}</Text>
  </View>
);

// ============ PAGE HEADER ============

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  meta?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, meta }) => (
  <View style={baseStyles.headerBanner}>
    <Text style={baseStyles.headerTitle}>{title}</Text>
    {subtitle && <Text style={baseStyles.headerSubtitle}>{subtitle}</Text>}
    {meta && <Text style={baseStyles.headerMeta}>{meta}</Text>}
  </View>
);

// ============ SECTION ============

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

export const Section: React.FC<SectionProps> = ({ title, children }) => (
  <View style={baseStyles.section}>
    <Text style={baseStyles.sectionTitle}>{title}</Text>
    {children}
  </View>
);

// ============ STATS ROW ============

interface StatItem {
  value: string | number;
  label: string;
  color?: string;
}

interface StatsRowProps {
  stats: StatItem[];
}

export const StatsRow: React.FC<StatsRowProps> = ({ stats }) => (
  <View style={baseStyles.statsRow}>
    {stats.map((stat, index) => (
      <View key={index} style={baseStyles.statBox}>
        <Text style={[baseStyles.statValue, stat.color ? { color: stat.color } : {}]}>
          {stat.value}
        </Text>
        <Text style={baseStyles.statLabel}>{stat.label}</Text>
      </View>
    ))}
  </View>
);

// ============ LIST ITEM ============

interface ListItemProps {
  children: React.ReactNode;
  bullet?: string;
  color?: string;
}

export const ListItem: React.FC<ListItemProps> = ({ children, bullet = '•', color }) => (
  <View style={baseStyles.listItem}>
    <Text style={[baseStyles.listBullet, color ? { color } : {}]}>{bullet}</Text>
    <Text style={baseStyles.listContent}>{children}</Text>
  </View>
);

// ============ TABLE ============

interface TableColumn {
  key: string;
  header: string;
  width?: string | number;
}

interface TableProps {
  columns: TableColumn[];
  data: Record<string, any>[];
  maxRows?: number;
}

export const Table: React.FC<TableProps> = ({ columns, data, maxRows }) => {
  const displayData = maxRows ? data.slice(0, maxRows) : data;
  
  return (
    <View style={baseStyles.table}>
      {/* Header */}
      <View style={baseStyles.tableHeader}>
        {columns.map((col, index) => (
          <Text 
            key={index} 
            style={[
              baseStyles.tableHeaderCell, 
              col.width ? { width: col.width } : { flex: 1 }
            ]}
          >
            {col.header}
          </Text>
        ))}
      </View>
      
      {/* Rows */}
      {displayData.map((row, rowIndex) => (
        <View key={rowIndex} style={baseStyles.tableRow}>
          {columns.map((col, colIndex) => (
            <Text 
              key={colIndex} 
              style={[
                baseStyles.tableCell, 
                col.width ? { width: col.width } : { flex: 1 }
              ]}
            >
              {row[col.key] ?? '-'}
            </Text>
          ))}
        </View>
      ))}
      
      {maxRows && data.length > maxRows && (
        <Text style={[baseStyles.textMuted, { marginTop: 4, textAlign: 'center' }]}>
          ... and {data.length - maxRows} more
        </Text>
      )}
    </View>
  );
};

// ============ CARD ============

interface CardProps {
  title?: string;
  children: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({ title, children }) => (
  <View style={baseStyles.card}>
    {title && <Text style={baseStyles.cardTitle}>{title}</Text>}
    {children}
  </View>
);

// ============ BADGE ============

interface BadgeProps {
  text: string;
  color?: string;
  backgroundColor?: string;
}

export const Badge: React.FC<BadgeProps> = ({ 
  text, 
  color = colors.white, 
  backgroundColor = colors.primary 
}) => (
  <View style={[baseStyles.badge, { backgroundColor }]}>
    <Text style={[baseStyles.badgeText, { color }]}>{text}</Text>
  </View>
);

// ============ CONNECTION LIST ============

interface Connection {
  type: string;
  targetLabel: string;
  targetId: string;
  count: number;
}

interface ConnectionListProps {
  connections: Connection[];
  groupByType?: boolean;
}

export const ConnectionList: React.FC<ConnectionListProps> = ({ connections, groupByType = true }) => {
  if (!groupByType) {
    return (
      <View>
        {connections.map((conn, index) => (
          <ListItem key={index} color={getConnectionColor(conn.type)}>
            {conn.targetLabel || conn.targetId} - {conn.count}x ({conn.type})
          </ListItem>
        ))}
      </View>
    );
  }
  
  // Group by type
  const grouped = connections.reduce((acc, conn) => {
    if (!acc[conn.type]) acc[conn.type] = [];
    acc[conn.type].push(conn);
    return acc;
  }, {} as Record<string, Connection[]>);
  
  const typeLabels: Record<string, string> = {
    call: 'Phone Calls',
    sms: 'Text Messages',
    browsing: 'Websites Visited',
    app: 'Applications',
    dwell: 'Locations',
  };
  
  return (
    <View>
      {Object.entries(grouped).map(([type, conns]) => (
        <View key={type} style={{ marginBottom: 8 }}>
          <Text style={[baseStyles.sectionSubtitle, { color: getConnectionColor(type) }]}>
            {typeLabels[type] || type} ({conns.length})
          </Text>
          {conns
            .sort((a, b) => b.count - a.count)
            .map((conn, index) => (
              <ListItem key={index} color={getConnectionColor(type)}>
                {conn.targetLabel || conn.targetId} - {conn.count}x
              </ListItem>
            ))}
        </View>
      ))}
    </View>
  );
};

// ============ NOTES LIST ============

interface Note {
  id: string;
  title: string;
  content: string;
  priority?: string;
  created_at?: string;
}

interface NotesListProps {
  notes: Note[];
}

export const NotesList: React.FC<NotesListProps> = ({ notes }) => (
  <View>
    {notes.map((note, index) => (
      <Card key={note.id || index}>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
          {note.priority && (
            <Badge 
              text={note.priority.toUpperCase()} 
              backgroundColor={getPriorityColor(note.priority)}
            />
          )}
          <Text style={baseStyles.cardTitle}>{note.title}</Text>
        </View>
        <Text style={baseStyles.text}>{note.content}</Text>
        {note.created_at && (
          <Text style={[baseStyles.textMuted, { marginTop: 4 }]}>
            Created: {new Date(note.created_at).toLocaleDateString()}
          </Text>
        )}
      </Card>
    ))}
  </View>
);

// ============ DIVIDER ============

export const Divider: React.FC = () => <View style={baseStyles.divider} />;

// ============ TWO COLUMN LAYOUT ============

interface TwoColumnProps {
  left: React.ReactNode;
  right: React.ReactNode;
}

export const TwoColumn: React.FC<TwoColumnProps> = ({ left, right }) => (
  <View style={baseStyles.twoColumn}>
    <View style={baseStyles.column}>{left}</View>
    <View style={baseStyles.column}>{right}</View>
  </View>
);

// ============ PAGE FOOTER ============

interface PageFooterProps {
  leftText?: string;
  showPageNumbers?: boolean;
  totalPages?: number;
  currentPage?: number;
}

export const PageFooter: React.FC<PageFooterProps> = ({ 
  leftText, 
  showPageNumbers = true,
}) => (
  <View style={baseStyles.footer}>
    <Text style={baseStyles.footerText}>{leftText || `Generated: ${new Date().toLocaleString()}`}</Text>
    {showPageNumbers && (
      <Text style={baseStyles.pageNumber} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} />
    )}
  </View>
);

// ============ IMAGE WITH CAPTION ============

interface ImageWithCaptionProps {
  src: string;
  caption?: string;
  maxWidth?: number;
  maxHeight?: number;
}

export const ImageWithCaption: React.FC<ImageWithCaptionProps> = ({ 
  src, 
  caption,
  maxWidth = 535,
  maxHeight = 300,
}) => (
  <View style={{ marginBottom: 10, alignItems: 'center' }}>
    <Image 
      src={src} 
      style={{ 
        maxWidth, 
        maxHeight, 
        objectFit: 'contain',
      }} 
    />
    {caption && (
      <Text style={[baseStyles.textMuted, { marginTop: 4, textAlign: 'center' }]}>
        {caption}
      </Text>
    )}
  </View>
);

// ============ CHAT MESSAGE ============

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ role, content, timestamp }) => (
  <View style={{ marginBottom: 10 }}>
    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
      <Badge 
        text={role === 'user' ? 'YOU' : 'SENTINEL'} 
        backgroundColor={role === 'user' ? colors.primary : colors.secondary}
      />
      {timestamp && (
        <Text style={[baseStyles.textMuted, { marginLeft: 8 }]}>{timestamp}</Text>
      )}
    </View>
    <Text style={baseStyles.text}>{content}</Text>
  </View>
);

// ============ EMPTY STATE ============

interface EmptyStateProps {
  message: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ message }) => (
  <View style={{ padding: 20, alignItems: 'center' }}>
    <Text style={[baseStyles.textMuted, { textAlign: 'center' }]}>{message}</Text>
  </View>
);


